import { ProductCard } from '@/components/ProductPage';
import React, { useEffect } from 'react';
import Image from 'next/image';
import { productsData } from '@/mocks/products';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { SEOHead } from '@/components/Shared';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const siteUrl = 'https://www.truongthofood.vn';

const ProductPage = () => {
  const router = useRouter();
  const freshEggs = productsData.filter(
    product => product.category === 'Trứng tươi'
  );
  const readyToEatEggs = productsData.filter(
    product => product.category === 'Trứng bóc vỏ sẵn'
  );

  useEffect(() => {
    if (router.asPath.includes('#')) {
      const hash = router.asPath.split('#')[1];
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [router.asPath]);

  // ItemList Schema for products
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Sản Phẩm Trứng Gà - Trường Thọ Food',
    description: 'Danh sách sản phẩm trứng gà tươi và trứng bóc vỏ sẵn chất lượng cao',
    itemListElement: productsData.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: product.name,
        description: product.description,
        image: product.thumbnail,
        url: `${siteUrl}/products/${product.slug}`,
        category: product.category,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="Sản Phẩm Trứng Gà - Trứng Tươi & Trứng Bóc Vỏ Sẵn | Trường Thọ Food"
        description="Khám phá danh sách sản phẩm trứng gà tươi và trứng bóc vỏ sẵn chất lượng cao của Trường Thọ Food. Từ trứng gà tươi loại 1 đến trứng gà chế biến sẵn, đảm bảo vệ sinh an toàn thực phẩm."
        keywords="sản phẩm trứng gà, trứng gà tươi, trứng bóc vỏ sẵn, trứng gà loại 1, trứng gà hữu cơ, trứng gà thả vườn, trứng gà luộc sẵn, trứng gà muối, danh sách sản phẩm trứng gà"
        image={`${siteUrl}/banner.png`}
        url={`${siteUrl}/products`}
        structuredData={itemListSchema}
      />
      <div>
      {/* Hero Banner */}
      <div className="flex w-full justify-center py-16 md:py-[84px] relative">
        <Image
          src={
            'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3'
          }
          alt="Banner trang sản phẩm trứng gà tươi và trứng bóc vỏ sẵn - Trường Thọ Food"
          fill
          style={{ objectFit: 'cover' }}
          className="absolute top-0 left-0 w-full h-full"
        />
        <div className="absolute top-0 left-0 w-full h-full bg-black opacity-50"></div>
        <p className="text-[24px] md:text-[32px] font-bold text-white z-10 relative uppercase">
          Sản phẩm của chúng tôi
        </p>
      </div>

      <div className="container py-8 md:py-16">
        {/* Section: Trứng tươi */}
        <motion.div
          id="trung-tuoi"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 md:mb-16 scroll-mt-24"
        >
          <h2 className="uppercase text-[24px] md:text-[32px] font-bold text-gray2 mb-8 md:mb-12 text-center">
            Trứng tươi
          </h2>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          >
            {freshEggs.map(product => (
              <motion.div key={product.id} variants={itemVariants}>
                <ProductCard {...product} />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Section: Trứng bóc vỏ sẵn */}
        <motion.div
          id="trung-an-lien"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="scroll-mt-24"
        >
          <h2 className="uppercase text-[24px] md:text-[32px] font-bold text-gray2 mb-8 md:mb-12 text-center">
            Trứng bóc vỏ sẵn
          </h2>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          >
            {readyToEatEggs.map(product => (
              <motion.div key={product.id} variants={itemVariants}>
                <ProductCard {...product} />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default ProductPage;
